import { useWorkflowTemplates } from '@commenda/api';
import { GetWorkflowTemplateListExtendsOptions } from '@commenda/types';
import { useConfig } from 'context/useConfig';
import { useCallback, useMemo } from 'react';

/** Data + routing for the affiliate workflow list: the templates the signed-in user created (the
 * accepted single-firm shortcut until templates carry a firm owner), plus the href builders that
 * keep every click on the affiliate editor rather than the client app's pages. */
export const useAffiliateWorkflows = () => {
  const { user, isLoading: isConfigLoading, affiliateFirmId } = useConfig();

  // VERSIONS rides along so a draft's row can still resolve to its newest version. `null` while
  // the user id is unknown: no unfiltered list fetch fires before the config resolves.
  const { data, error, isLoading, mutate } = useWorkflowTemplates(
    user?.id
      ? {
          createdByUserId: user.id,
          extends: [GetWorkflowTemplateListExtendsOptions.VERSIONS],
        }
      : null
  );

  const workflows = useMemo(
    () => data?.workflowTemplates ?? [],
    [data?.workflowTemplates]
  );

  const getEditorHref = useCallback(
    (versionId: string) =>
      `/affiliate/${affiliateFirmId}/workflows/version/${versionId}`,
    [affiliateFirmId]
  );

  // A workflow with no version has no editor to open — undefined tells the list to skip navigation
  // instead of pushing a same-page no-op.
  const getWorkflowHref = useCallback(
    (workflowTemplateId: string) => {
      const workflow = workflows.find(
        (template) => template.id === workflowTemplateId
      );
      const versionId =
        workflow?.activeVersionId ?? workflow?.versions?.[0]?.id;
      return versionId ? getEditorHref(versionId) : undefined;
    },
    [workflows, getEditorHref]
  );

  return {
    workflows,
    error,
    mutate,
    getEditorHref,
    getWorkflowHref,
    isLoading: isConfigLoading || isLoading,
    /** The config finished loading yet produced no user — an auth/config failure to surface, not a
     * pending state to spin on. */
    isUserMissing: !isConfigLoading && !user?.id,
  };
};
